import { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { connect } from 'react-redux';
import { Link } from 'react-router';

import { HYDRA_RET } from 'constants/hydraTests';
import { evOnShowRewardsTransparencyClick } from 'events/global';

import { pluralize } from 'helpers';
import ProductUtils from 'helpers/ProductUtils';
import { track } from 'apis/amethyst';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { isAssigned, triggerAssignment } from 'actions/ab';
import stars from 'images/stars.svg';
import css from 'styles/components/common/rewardsTransparency.scss';

export class RewardsTransparency extends Component {
  static contextTypes = {
    testId: PropTypes.func,
    marketplace: PropTypes.object
  }

  componentDidMount() {
    const { triggerAssignment } = this.props;
    triggerAssignment(HYDRA_RET);
  }

  handleCtaClick = () => {
    const { isForCartModal, pageType, isRecognizedCustomer, pointsForCurrentPage, rewards } = this.props;
    track(evOnShowRewardsTransparencyClick, { isForCartModal, pageType, rewards, isRecognizedCustomer, pointsForCurrentPage });
  }

  render() {
    const { handleCtaClick } = this;
    const {
      isAssignedInRewardsTransparency,
      isForCartModal,
      pageType,
      isRecognizedCustomer,
      rewards,
      pointsForCurrentPage,
      cartCount,
      isGiftCard
    } = this.props;
    const { testId, marketplace: { hasRewardsTransparency } } = this.context;

    if (!hasRewardsTransparency || !isAssignedInRewardsTransparency || (pageType === 'product' && isGiftCard)) {
      return null;
    }

    const rewardsText = makeRewardsText({ handleCtaClick, isForCartModal, pageType, rewards, pointsForCurrentPage, isRecognizedCustomer, cartCount });

    return !!rewardsText && (
      <div
        className={cn(css.page, {
          [css.cart]: pageType === 'cart' && !isForCartModal,
          [css.pdp]: pageType === 'product' && !isForCartModal,
          [css.brand]: pageType === 'brand' && !isForCartModal,
          [css.checkout]: pageType === 'checkout' && !isForCartModal,
          [css.cartModal]: !!isForCartModal
        })}
        data-test-id={testId('rewardsTransparencyContainer')}>
        <img src={stars} alt="stars" />
        {rewardsText}
      </div>
    );
  }
}

const makeRewardsText = ({ handleCtaClick, isForCartModal, pageType, isRecognizedCustomer, pointsForCurrentPage, rewards: { isEnrolled, transparencyPointsForCart }, cartCount }) => {
  if (pointsForCurrentPage && !isForCartModal && ['product', 'cart'].includes(pageType)) {

    const isPlural = pageType !== 'cart' ? 1 : cartCount;

    if (isRecognizedCustomer && isEnrolled) {
      return (
        <p>
          Earn <strong>{pointsForCurrentPage} in Zappos Rewards points</strong> when you purchase {pluralize('this', isPlural)} {pluralize('item', isPlural)}!
        </p>
      );
    }
    if (isRecognizedCustomer && !isEnrolled) {
      return (
        <p>
          Earn <strong>{pointsForCurrentPage} in Zappos Rewards points</strong> on {pluralize('this', isPlural)} {pluralize('item', isPlural)}. Not a Rewards member?  <Link onClick={handleCtaClick} to="/rewards/dashboard">Join today to start earning!</Link>
        </p>
      );
    }
    return (
      <p>
        Earn <strong>{pointsForCurrentPage} in Zappos Rewards points</strong> on {pluralize('this', isPlural)} {pluralize('item', isPlural)}. <Link onClick={handleCtaClick} to="/rewards/dashboard">Sign in to get started!</Link>
      </p>
    );
  }

  if (transparencyPointsForCart && isForCartModal) {
    return (
      <p>
        Check out to earn <span>{transparencyPointsForCart} Rewards points</span>
      </p>
    );
  }

  return null;
};

const pointsToPageMap = {
  'cart': 'transparencyPointsForCart',
  'checkout': 'transparencyPointsForCart',
  'homepage': 'transparencyPointsForBrand',
  'landing': 'transparencyPointsForBrand',
  'product': 'transparencyPointsForItem'
};

const mapStateToProps = state => {
  const { cart: { isModalShowing, cartCount }, product, sharedRewards: rewards, cookies: { 'x-main': xMain }, pageView: { pageType } } = state;
  const isAssignedInRewardsTransparency = isAssigned(HYDRA_RET, 1, state);
  const isRecognizedCustomer = !!xMain;
  const pointsForCurrentPage = rewards[pointsToPageMap[pageType]];

  return {
    rewards,
    pageType,
    pointsForCurrentPage,
    isAssignedInRewardsTransparency,
    isRecognizedCustomer,
    isModalShowing,
    cartCount,
    triggerAssignment,
    isGiftCard: ProductUtils.isGiftCard(product?.detail?.defaultProductType)
  };
};

RewardsTransparency.displayName = 'RewardsTransparency';

export default withErrorBoundary('RewardsTransparency', connect(mapStateToProps)(RewardsTransparency));
